import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Heading1Text, BodySmall } from '../../../components/Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { useGetGiftCardCatalogueQuery } from '../../../redux/api/public/globetopper';
import {
  formatUSDEnsure2Digits,
  getCurrencySymbol,
} from '../../../common/currency';
import { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectValidationResult } from '../../../redux/slices/sessionValidation';
import { useGetMyGiftCardsQuery } from '../../../redux/api/authorized/globetopper';
import { selectPrivyToken } from '../../../redux/slices/auth';
import {
  selectGiftCardsForCountry,
  selectSelectedCountryCode,
  setSelectedCountryCode,
} from '../../../redux/slices/giftCardCatalogue';
import SpendGalleryGiftCard from './SpendGalleryGiftCard';
import ScrollRestore from '../../../components/ScrollRestore';
import SearchIcon from '../../../assets/icons/search.svg?react';
import CashbackInfo from '../../../components/CashbackInfo';

export const categories = [
  { id: 0, name: "All", description: "All" },
  { id: 1, name: "Most Popular", description: "Most Popular" },
  { id: 2, name: "E-commerce", description: "E-commerce" },
  { id: 3, name: "Food & Groceries", description: "Food & Groceries" },
  { id: 4, name: "Restaurants & Cafés", description: "Restaurants & Cafés" },
  { id: 5, name: "Electronics", description: "Electronics" },
  { id: 6, name: "Gaming", description: "Gaming" },
  { id: 7, name: "Fashion & Accessories", description: "Fashion & Accessories" },
  { id: 8, name: "Health & Beauty", description: "Health & Beauty" },
  { id: 9, name: "Travel & Hotels", description: "Travel & Hotels" },
  { id: 10, name: "Entertainment & Experiences", description: "Entertainment & Experiences" },
  { id: 11, name: "Sports & Outdoors", description: "Sports & Outdoors" },
  { id: 12, name: "Home & DIY", description: "Home & DIY" },
  { id: 13, name: "Transport & Fuel", description: "Transport & Fuel" },
  { id: 14, name: "Kids & Toys", description: "Kids & Toys" },
  { id: 15, name: "Department Stores", description: "Department Stores" },
  { id: 16, name: "Multi-Brand Cards", description: "Multi-Brand Cards" },
  { id: 17, name: "Education & Learning", description: "Education & Learning" },
  { id: 18, name: "Other", description: "Other" },
];

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const SearchContainer = styled.div`
  width: 100%;
  margin-top: 24px;
  margin-bottom: 12px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 0px;
    margin-bottom: 5px;
    margin-top: 5px;
  }
`;

const MyPurchasesWrapper = styled.div`
  margin-bottom: 48px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-bottom: 32px;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 12px;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.secondary};
  color: ${({ theme }) => theme.text.title};
  font-size: 16px;
  outline: none;
  transition: all 0.2s;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;

  &:focus {
    border-color: ${({ theme }) => theme.primary.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primary.background};
  }

  option {
    background-color: ${({ theme }) => theme.background.secondary};
    color: ${({ theme }) => theme.text.title};
  }
`;

const SelectWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 48px 0 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.secondary};
  color: ${({ theme }) => theme.text.title};
  font-size: 16px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.primary.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primary.background};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text.secondary};
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.text.secondary};
`;

const SectionContainer = styled.div`
  background-color: ${({ $isAlternate, theme }) =>
    $isAlternate
      ? theme.background.secondary || theme.background.primary
      : 'transparent'};
  padding: 20px 0;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  --section-bg: ${({ $isAlternate, theme }) =>
    $isAlternate
      ? theme.background.secondary || theme.background.primary
      : theme.background.primary};
  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px 0 20px 20px;
  }
`;

const SectionContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
    padding: 0 20px 0 0;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.text.secondary};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MySpendCard = styled.button`
  background: none;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.primary};
  flex: 0 0 250px;
  min-width: 350px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
    flex: 1 1 auto;
    min-width: auto;
  }
`;

const MySpendInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MySpendImage = styled.img`
  width: 100px;
  border-radius: 8px;
  object-fit: cover;
  background-color: ${({ theme }) =>
    theme.background.secondary || theme.background.primary};
  @media ${PHONE_MEDIA_QUERY} {
    width: 80px;
  }
`;

const MySpendText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 2px;
`;

const MySpendName = styled(BodySmall)`
  color: ${({ theme }) => theme.text.title};
  margin: 0;
`;

const MySpendAmount = styled(BodySmall)`
  color: ${({ theme }) => theme.text.secondary};
  margin: 0;
`;

const MySpendAction = styled.span`
  color: ${({ theme }) => theme.text.secondary};
  margin: 0;
`;

const MySpendScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const MySpendRow = styled.div`
  display: flex;
  gap: 16px;
  width: fit-content;
`;

const MySpendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const DESKTOP_MAX_CATEGORY_CARDS = 4;
const MOBILE_MAX_CATEGORY_CARDS = 6;

const MyPurchasesSection = ({ items, onSeeMore }) => {
  if (!items || items.length === 0) return null;

  const navigate = useNavigate();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  return (
    <MyPurchasesWrapper>
      <Header>
        <SectionHeaderRow>
          <SpendTitle>My Purchases</SpendTitle>
          <SeeMoreButton type='button' onClick={onSeeMore}>
            <BodySmall style={{ color: 'inherit' }}>See All</BodySmall>
            <BodySmall style={{ color: 'inherit' }}>&gt;</BodySmall>
          </SeeMoreButton>
        </SectionHeaderRow>
        {isMobile ? (
          <MySpendContainer>
            {items.length > 0 &&
              (() => {
                const giftCard = items[items.length - 1];
                const name = giftCard.name;
                const imageUrl = giftCard.imageUrl;
                const localAmount = giftCard.localAmount;
                const currencySymbol = getCurrencySymbol(giftCard.currency);

                return (
                  <MySpendCard
                    key={giftCard.id || `${name}-${imageUrl}`}
                    type='button'
                    onClick={() => navigate('/app/spend/my-cards')}
                  >
                    <MySpendInfo>
                      {imageUrl ? (
                        <MySpendImage src={imageUrl} alt={name} />
                      ) : null}
                      <MySpendText>
                        <MySpendName>{name}</MySpendName>
                        {localAmount ? (
                          <MySpendAmount>
                            {currencySymbol}
                            {Number(localAmount).toFixed(2)}
                          </MySpendAmount>
                        ) : null}
                      </MySpendText>
                    </MySpendInfo>
                    <MySpendAction>
                      <BodySmall style={{ color: 'inherit' }}>View</BodySmall>
                    </MySpendAction>
                  </MySpendCard>
                );
              })()}
          </MySpendContainer>
        ) : (
          <MySpendScroll>
            <MySpendRow>
              {items.map((giftCard) => {
                const name = giftCard.name;
                const imageUrl = giftCard.imageUrl;
                const localAmount = giftCard.localAmount;
                const currencySymbol = getCurrencySymbol(giftCard.currency);

                return (
                  <MySpendCard
                    key={giftCard.id || `${name}-${imageUrl}`}
                    type='button'
                    onClick={() => navigate('/app/spend/my-cards')}
                  >
                    <MySpendInfo>
                      {imageUrl ? (
                        <MySpendImage src={imageUrl} alt={name} />
                      ) : null}
                      <MySpendText>
                        <MySpendName>{name}</MySpendName>
                        {localAmount ? (
                          <MySpendAmount>
                            {currencySymbol}
                            {Number(localAmount).toFixed(2)}
                          </MySpendAmount>
                        ) : null}
                      </MySpendText>
                    </MySpendInfo>
                    <MySpendAction>
                      <BodySmall style={{ color: 'inherit' }}>View</BodySmall>
                    </MySpendAction>
                  </MySpendCard>
                );
              })}
            </MySpendRow>
          </MySpendScroll>
        )}
      </Header>
    </MyPurchasesWrapper>
  );
};

const SpendTitle = styled(Heading1Text)`
  font-size: 24px;
  line-height: 28px;
  margin: 0;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    cursor: pointer;
  }
`;

const ShopsAndBrandsTitle = styled(Heading1Text)`
  font-size: 28px;
  line-height: 32px;
  margin: 0;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const ScrollInner = styled.div`
  display: flex;
  gap: 16px;
  width: fit-content;
  flex-wrap: nowrap;
  padding-right: 16px;
`;

const CarouselSlide = styled.div`
  display: flex;
  gap: 16px;
  min-width: calc(100vw - 32px);
  align-items: stretch;
  @media ${PHONE_MEDIA_QUERY} {
    min-width: auto;
    width: fit-content;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: repeat(auto-fill, minmax(45%, 1fr));
    gap: 16px;
  }
`;

// Loading skeleton component
const LoadingSkeleton = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-right: 16px;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const SkeletonSlide = styled.div`
  display: flex;
  gap: 16px;
  min-width: calc(100vw - 32px);
  flex-shrink: 0;
`;

const SkeletonCard = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  height: 300px;
  animation: pulse 1.5s ease-in-out infinite;

  @media ${PHONE_MEDIA_QUERY} {
    flex: 0 0 calc(50% - 8px);
    min-width: calc(50% - 8px);
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// Helper function to format denomination to price range (exported for Hero spend section)
export const formatPriceRange = (card) => {
  if (!card) return '';

  // Prefer detailedInfo.min and detailedInfo.max if available (cleaner, no parsing needed)
  if (card.detailedInfo?.min && card.detailedInfo?.max) {
    const min = Math.round(parseFloat(card.detailedInfo.min.replace(/,/g, '')));
    const max = Math.round(parseFloat(card.detailedInfo.max.replace(/,/g, '')));
    const currencySymbol = getCurrencySymbol(
      card.detailedInfo?.currency?.code || card.currency,
    );

    return `${currencySymbol}${min} - ${currencySymbol}${max}`;
  }

  // Fall back to parsing denomination string
  if (!card.denomination) return '';

  // Extract the range (handle "or" clauses by taking the first range)
  const firstRange = card.denomination.split(' or ')[0];
  // Match numbers with optional commas and decimals: "5.00 - 1,000.00 by 0.01"
  const match = firstRange.match(
    /([\d,]+\.?\d*)\s*-\s*([\d,]+\.?\d*)\s*by\s*[\d,]+\.?\d*/,
  );

  if (!match) return card.denomination;

  const [, minStr, maxStr] = match;

  // Parse and round to whole numbers (remove commas first)
  const min = Math.round(parseFloat(minStr.replace(/,/g, '')));
  const max = Math.round(parseFloat(maxStr.replace(/,/g, '')));
  const currencySymbol = getCurrencySymbol(card.currency);

  return `${currencySymbol}${min}-${currencySymbol}${max}`;
};

const SpendGallery = ({
  excludeCategoryIds = [],
  hideCashbackInfo = false,
  hideSearchBar = false,
  hideCategoryFilter = false,
} = {}) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const validationResult = useSelector(selectValidationResult);
  const dispatch = useDispatch();
  const selectedCountryCode = useSelector(selectSelectedCountryCode);
  const privyToken = useSelector(selectPrivyToken);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);

  const effectiveSearchTerm = searchTerm;
  const effectiveCategoryId = hideCategoryFilter ? 0 : selectedCategoryId;
  const showSearchContainer = true;
  const { data: myGiftCardsData } = useGetMyGiftCardsQuery(undefined, {
    skip: !privyToken,
  });

  // Initialize country code from validation result (Redux is source of truth)
  useEffect(() => {
    if (selectedCountryCode) return;
    const countryCode = validationResult?.countryCode;
    if (countryCode) {
      const normalizedCode = countryCode.toUpperCase();
      dispatch(setSelectedCountryCode(normalizedCode));
    }
  }, [validationResult?.countryCode, selectedCountryCode, dispatch]);

  // Use empty string instead of null for RTK Query cache key consistency
  const queryCountryCode = selectedCountryCode || '';
  const { isLoading, isFetching, error } =
    useGetGiftCardCatalogueQuery(queryCountryCode);
  const isLoadingCards = isLoading || isFetching;

  // Get gift cards from normalized store
  const regularGiftCards = useSelector((state) =>
    selectGiftCardsForCountry(state, queryCountryCode),
  );
  const giftCards = regularGiftCards;

  const myGiftCards = useMemo(() => {
    if (Array.isArray(myGiftCardsData)) return myGiftCardsData;
    return myGiftCardsData?.records || myGiftCardsData?.giftCards || [];
  }, [myGiftCardsData]);

  const myPurchaseItems = myGiftCards.slice(0, 4);

  // Filter cards by category
  const filteredByCategoryCards = useMemo(() => {
    if (!giftCards || giftCards.length === 0) return [];
    if (effectiveCategoryId === 0) return giftCards; // "All" category

    return giftCards.filter((card) => {
      const categories = card.detailedInfo?.categories || [];
      return categories.some((category) => category.id === effectiveCategoryId);
    });
  }, [giftCards, effectiveCategoryId]);

  // Filter cards by search term
  const filteredSearchCards = useMemo(() => {
    if (!filteredByCategoryCards || filteredByCategoryCards.length === 0 || !effectiveSearchTerm.trim()) return [];

    const searchLower = effectiveSearchTerm.toLowerCase();
    return filteredByCategoryCards.filter((card) => {
      const brand = (card.brand || '').toLowerCase();
      const name = (card.name || '').toLowerCase();
      return brand.includes(searchLower) || name.includes(searchLower);
    });
  }, [filteredByCategoryCards, effectiveSearchTerm]);

  // Group cards by category from backend
  const categorySections = useMemo(() => {
    if (!filteredByCategoryCards || filteredByCategoryCards.length === 0) return [];

    // If a specific category is selected, only show that category section
    if (effectiveCategoryId !== 0) {
      const selectedCategory = categories.find((cat) => cat.id === effectiveCategoryId);
      if (!selectedCategory) return [];

      return [
        {
          category: {
            id: selectedCategory.id,
            name: selectedCategory.name,
            displayName: selectedCategory.name,
          },
          cards: filteredByCategoryCards,
          slides: (() => {
            const slides = [];
            for (let i = 0; i < filteredByCategoryCards.length; i += 2) {
              slides.push(filteredByCategoryCards.slice(i, i + 2));
            }
            return slides;
          })(),
        },
      ];
    }

    // Create a map to group cards by category (for "All" view)
    const categoryMap = new Map();

    filteredByCategoryCards.forEach((card) => {
      const categories = card.detailedInfo?.categories;

      categories?.forEach((category) => {
        if (!category || category.id === undefined || category.id === null) {
          return;
        }

        const categoryId = category.id;
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            category: {
              id: category.id,
              name: category.name,
              displayName: category.displayName || category.name,
            },
            cards: [],
            cardIds: new Set(),
          });
        }

        const entry = categoryMap.get(categoryId);
        const cardId = String(card.id);
        if (!entry.cardIds.has(cardId)) {
          entry.cardIds.add(cardId);
          entry.cards.push(card);
        }
      });
    });

    // Convert map to array and sort by category id
    const sections = Array.from(categoryMap.values())
      .map((section) => ({
        category: section.category,
        cards: section.cards,
      }))
      .sort((a, b) => a.category.id - b.category.id);

    // Group cards into slides of 2 (1 row) for each section
    return sections.map((section) => {
      const slides = [];
      for (let i = 0; i < section.cards.length; i += 2) {
        slides.push(section.cards.slice(i, i + 2));
      }

      return {
        ...section,
        slides,
      };
    });
  }, [filteredByCategoryCards, effectiveCategoryId]);

  if (isLoadingCards) {
    return (
      <Container>
        <ScrollRestore />
        <MyPurchasesSection
          items={myPurchaseItems}
          onSeeMore={() => navigate('/app/spend/my-cards')}
        />
        {showSearchContainer && (
          <SearchContainer>
            <FiltersContainer>
              <SearchInputWrapper>
                <SearchInput
                  type='text'
                  placeholder='Search shops and brands...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled
                />
                <SearchIconWrapper>
                  <SearchIcon width={20} height={20} />
                </SearchIconWrapper>
              </SearchInputWrapper>
              {!hideCategoryFilter && (
                <SelectWrapper>
                  <SelectInput
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </SelectInput>
                </SelectWrapper>
              )}
            </FiltersContainer>
          </SearchContainer>
        )}
        <Header>
          <SpendTitle>Most Popular Shops</SpendTitle>
        </Header>
        {isMobile ? (
          <LoadingSkeleton>
            {[...Array(2)].map((_, slideIndex) => (
              <SkeletonSlide key={slideIndex}>
                {[...Array(2)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </SkeletonSlide>
            ))}
          </LoadingSkeleton>
        ) : (
          <GridContainer>
            {[...Array(8)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </GridContainer>
        )}
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ScrollRestore />
        <MyPurchasesSection
          items={myPurchaseItems}
          onSeeMore={() => navigate('/app/spend/my-cards')}
        />
        {showSearchContainer && (
          <SearchContainer>
            <FiltersContainer>
              <SearchInputWrapper>
                <SearchInput
                  type='text'
                  placeholder='Search shops and brands...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled
                />
                <SearchIconWrapper>
                  <SearchIcon width={20} height={20} />
                </SearchIconWrapper>
              </SearchInputWrapper>
              {!hideCategoryFilter && (
                <SelectWrapper>
                  <SelectInput
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </SelectInput>
                </SelectWrapper>
              )}
            </FiltersContainer>
          </SearchContainer>
        )}
        <Header>
          <SpendTitle>Most Popular</SpendTitle>
        </Header>
      </Container>
    );
  }

  // Check if we have any cards at all
  const hasAnyCards = categorySections.some(
    (section) => section.cards.length > 0,
  );

  // If no cards match categories, show all cards in "Most Popular" section
  if (!hasAnyCards && filteredByCategoryCards.length > 0) {
    if (isMobile) {
      const allSlides = [];
      for (let i = 0; i < filteredByCategoryCards.length; i += 2) {
        allSlides.push(filteredByCategoryCards.slice(i, i + 2));
      }

      return (
        <Container>
          <ScrollRestore />
          <MyPurchasesSection
            items={myPurchaseItems}
            onSeeMore={() => navigate('/app/spend/my-cards')}
          />
          {showSearchContainer && (
            <SearchContainer>
              <FiltersContainer>
                <SearchInputWrapper>
                  <SearchInput
                    type='text'
                    placeholder='Search shops and brands...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <SearchIconWrapper>
                    <SearchIcon width={20} height={20} />
                  </SearchIconWrapper>
                </SearchInputWrapper>
                {!hideCategoryFilter && (
                  <SelectWrapper>
                    <SelectInput
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </SelectInput>
                  </SelectWrapper>
                )}
              </FiltersContainer>
            </SearchContainer>
          )}
          <div>
            <Header>
              <SpendTitle>Most Popular</SpendTitle>
            </Header>
            <ScrollContainer>
              <ScrollInner>
                {allSlides.map((slide, slideIndex) => (
                  <CarouselSlide key={slideIndex}>
                    {slide.map((card) => (
                      <SpendGalleryGiftCard
                        key={card.id}
                        card={card}
                        priceRange={formatPriceRange(card)}
                      />
                    ))}
                  </CarouselSlide>
                ))}
              </ScrollInner>
            </ScrollContainer>
          </div>
        </Container>
      );
    }

    // Desktop: show as grid
    return (
      <Container>
        <ScrollRestore />
        <MyPurchasesSection
          items={myPurchaseItems}
          onSeeMore={() => navigate('/app/spend/my-cards')}
        />
        {showSearchContainer && (
          <SearchContainer>
            <FiltersContainer>
              <SearchInputWrapper>
                <SearchInput
                  type='text'
                  placeholder='Search shops and brands...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchIconWrapper>
                  <SearchIcon width={20} height={20} />
                </SearchIconWrapper>
              </SearchInputWrapper>
              {!hideCategoryFilter && (
                <SelectWrapper>
                  <SelectInput
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </SelectInput>
                </SelectWrapper>
              )}
            </FiltersContainer>
          </SearchContainer>
        )}
        <Header>
          <SpendTitle>Most Popular Shops</SpendTitle>
        </Header>
        <GridContainer>
          {filteredByCategoryCards.map((card) => (
            <SpendGalleryGiftCard
              key={card.id}
              card={card}
              priceRange={formatPriceRange(card)}
            />
          ))}
        </GridContainer>
      </Container>
    );
  }

  return (
    <Container>
      <ScrollRestore />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          marginBottom: '20px',
          marginTop: '15px',
        }}
      >
        {hideCashbackInfo ? (
          <ShopsAndBrandsTitle $variant="text-title">Explore Shops and Brands</ShopsAndBrandsTitle>
        ) : (
          <Heading1Text $variant="text-title">Explore Shops and Brands</Heading1Text>
        )}
        {!hideCashbackInfo && <CashbackInfo />}
      </div>
      <MyPurchasesSection
        items={myPurchaseItems}
        onSeeMore={() => navigate('/app/spend/my-cards')}
      />
      {showSearchContainer && (
        <SearchContainer>
          <FiltersContainer>
            <SearchInputWrapper>
              <SearchInput
                type='text'
                placeholder='Search shops and brands...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIconWrapper>
                <SearchIcon width={20} height={20} />
              </SearchIconWrapper>
            </SearchInputWrapper>
            {!hideCategoryFilter && (
              <SelectWrapper>
                <SelectInput
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </SelectInput>
              </SelectWrapper>
            )}
          </FiltersContainer>
        </SearchContainer>
      )}
      {effectiveSearchTerm.trim() ? (
        <SectionContainer>
          <SectionContent>
            <Header>
              <SpendTitle>
                {filteredSearchCards.length > 0
                  ? 'Results'
                  : 'No results found'}
              </SpendTitle>
            </Header>
            {filteredSearchCards.length > 0 ? (
              <GridContainer>
                {filteredSearchCards.map((card) => (
                  <SpendGalleryGiftCard
                    key={card.id}
                    card={card}
                    priceRange={formatPriceRange(card)}
                  />
                ))}
              </GridContainer>
            ) : null}
          </SectionContent>
        </SectionContainer>
      ) : (
        categorySections
          .filter(
            (section) =>
              !excludeCategoryIds.length ||
              !excludeCategoryIds.includes(section.category.id),
          )
          .map((section, sectionIndex) => {
          if (section.cards.length === 0) return null;

          const isAlternate = sectionIndex % 2 === 1;
          const displayName =
            section.category.displayName || section.category.name;
          const maxCategoryCards = isMobile
            ? MOBILE_MAX_CATEGORY_CARDS
            : DESKTOP_MAX_CATEGORY_CARDS;
          const categoryPath = encodeURIComponent(displayName);
          const showSeeAll = section.cards.length > maxCategoryCards;
          const visibleCards = section.cards.slice(0, maxCategoryCards);
          const visibleSlides = section.slides
            .map((slide) => slide.slice(0, 2))
            .filter((slide) => slide.length > 0)
            .slice(0, Math.ceil(maxCategoryCards / 2));

          return (
            <SectionContainer
              key={section.category.id}
              $isAlternate={isAlternate}
            >
              <SectionContent>
                <Header>
                  <SectionHeaderRow>
                    <SpendTitle>{displayName}</SpendTitle>
                    {showSeeAll && (
                      <SeeMoreButton
                        type='button'
                        onClick={() =>
                          navigate(`/app/spend/category/${categoryPath}`)
                        }
                        style={isMobile ? { marginRight: '15px' } : undefined}
                      >
                        <BodySmall style={{ color: 'inherit' }}>
                          See All
                        </BodySmall>
                        <BodySmall style={{ color: 'inherit' }}>&gt;</BodySmall>
                      </SeeMoreButton>
                    )}
                  </SectionHeaderRow>
                </Header>
                {isMobile ? (
                  // Mobile: horizontal scroll carousel
                  <ScrollContainer>
                    <ScrollInner>
                      {visibleSlides.map((slide, slideIndex) => (
                        <CarouselSlide key={slideIndex}>
                          {slide.map((card) => (
                            <SpendGalleryGiftCard
                              key={card.id}
                              card={card}
                              priceRange={formatPriceRange(card)}
                            />
                          ))}
                        </CarouselSlide>
                      ))}
                    </ScrollInner>
                  </ScrollContainer>
                ) : (
                  // Desktop: grid layout
                  <GridContainer>
                    {visibleCards.map((card) => (
                      <SpendGalleryGiftCard
                        key={card.id}
                        card={card}
                        priceRange={formatPriceRange(card)}
                      />
                    ))}
                  </GridContainer>
                )}
              </SectionContent>
            </SectionContainer>
          );
        })
      )}
    </Container>
  );
};

export default SpendGallery;
